// ============================================
// REST 数据源适配器
// 支持轮询和单次请求
// ============================================

import type { DatasourceAdapter } from '../../types/databinding';
import type { DatasourceConfig, RestConfig } from '../../types/designer';
import { EventEmitter } from '../EventEmitter';

export class RestAdapter extends EventEmitter implements DatasourceAdapter {
  readonly type = 'rest' as const;
  private status: 'disconnected' | 'connecting' | 'connected' | 'error' = 'disconnected';
  private timers: Map<string, ReturnType<typeof setInterval>> = new Map();
  private abortControllers: Map<string, AbortController> = new Map();
  private config: DatasourceConfig | null = null;

  async connect(config: DatasourceConfig): Promise<void> {
    if (config.type !== 'rest') throw new Error('Invalid config type');
    this.config = config;
    this.status = 'connecting';
    this.emit('status:change', this.status);

    // 立即执行一次请求验证连通性
    try {
      await this.fetchOnce(config.config as RestConfig);
      this.status = 'connected';
      this.emit('status:change', this.status);
    } catch (err: any) {
      this.status = 'error';
      this.emit('status:change', this.status);
      this.emit('error', err.message);
      throw err;
    }
  }

  disconnect(): void {
    this.timers.forEach((timer) => clearInterval(timer));
    this.timers.clear();
    this.abortControllers.forEach((c) => c.abort());
    this.abortControllers.clear();
    this.status = 'disconnected';
    this.config = null;
    this.emit('status:change', this.status);
  }

  subscribe(topic: string, callback: (data: any) => void): void {
    if (!this.config || this.config.type !== 'rest') return;
    const restConfig = this.config.config as RestConfig;
    const interval = restConfig.interval ?? 5000;

    // 保存回调以便轮询时调用
    const wrappedCallback = async () => {
      try {
        const data = await this.fetchOnce(restConfig);
        callback(data);
      } catch (err: any) {
        this.emit('error', err.message);
      }
    };

    // 立即执行一次
    wrappedCallback();

    // 设置轮询
    if (interval > 0) {
      const timer = setInterval(wrappedCallback, interval);
      this.timers.set(topic, timer);
    }
    this.emit('subscribe', { topic });
  }

  unsubscribe(topic: string): void {
    const timer = this.timers.get(topic);
    if (timer) {
      clearInterval(timer);
      this.timers.delete(topic);
    }
    this.emit('unsubscribe', { topic });
  }

  publish(topic: string, data: any): void {
    // REST 通常不支持推送，但可以 POST 数据
    if (!this.config || this.config.type !== 'rest') return;
    const restConfig = this.config.config as RestConfig;
    if (restConfig.method === 'POST') {
      this.fetchOnce({ ...restConfig, body: data }).catch(() => {});
    }
  }

  getStatus() {
    return this.status;
  }

  private async fetchOnce(config: RestConfig): Promise<any> {
    const controller = new AbortController();
    this.abortControllers.set('fetch', controller);

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...config.headers,
      };

      const init: RequestInit = {
        method: config.method,
        headers,
        signal: controller.signal,
      };

      if (config.method === 'POST' && config.body) {
        init.body = typeof config.body === 'string' ? config.body : JSON.stringify(config.body);
      }

      const response = await fetch(config.url, init);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        return await response.json();
      }
      return await response.text();
    } finally {
      this.abortControllers.delete('fetch');
    }
  }
}
